import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Portal.css';

function ThirdPartyPortal({ user }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('transactions');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  const roleLabels = {
    title_company: 'Title Company',
    lender: 'Lender',
    inspector: 'Inspector',
    escrow_agent: 'Escrow Agent',
    other: 'Partner'
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/third-party/transactions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.transactions || []);
      setTransactions(data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (e, transactionId) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('document', file);

    setUploadingDocument(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/third-party/transactions/${transactionId}/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert('Document uploaded successfully!');
      fetchTransactions();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const getRoleLabel = () => {
    return roleLabels[user?.thirdPartyRole] || 'Partner';
  };

  const getTasksForRole = () => {
    const tasks = {
      title_company: [
        { id: 1, task: 'Perform title search', status: 'pending' },
        { id: 2, task: 'Prepare title commitment', status: 'pending' },
        { id: 3, task: 'Clear any title defects', status: 'pending' },
        { id: 4, task: 'Issue title insurance policy', status: 'pending' },
        { id: 5, task: 'Coordinate closing documents', status: 'pending' }
      ],
      lender: [
        { id: 1, task: 'Review loan application', status: 'pending' },
        { id: 2, task: 'Order appraisal', status: 'pending' },
        { id: 3, task: 'Verify employment and income', status: 'pending' },
        { id: 4, task: 'Underwrite loan', status: 'pending' },
        { id: 5, task: 'Issue clear to close', status: 'pending' },
        { id: 6, task: 'Fund the loan', status: 'pending' }
      ],
      inspector: [
        { id: 1, task: 'Schedule inspection', status: 'pending' },
        { id: 2, task: 'Perform property inspection', status: 'pending' },
        { id: 3, task: 'Document findings with photos', status: 'pending' },
        { id: 4, task: 'Prepare inspection report', status: 'pending' },
        { id: 5, task: 'Deliver report to buyer', status: 'pending' }
      ],
      escrow_agent: [
        { id: 1, task: 'Open escrow account', status: 'pending' },
        { id: 2, task: 'Receive earnest money deposit', status: 'pending' },
        { id: 3, task: 'Coordinate with all parties', status: 'pending' },
        { id: 4, task: 'Prepare closing statement', status: 'pending' },
        { id: 5, task: 'Disburse funds at closing', status: 'pending' }
      ]
    };
    return tasks[user?.thirdPartyRole] || [];
  };

  if (!user) {
    return <div className="portal"><p>Loading...</p></div>;
  }

  return (
    <div className="portal">
      <div className="portal-header">
        <h1>{getRoleLabel()} Portal</h1>
        <p>Welcome back, {user.firstName}! Manage your assigned transactions.</p>
      </div>

      <div className="stats-dashboard">
        <div className="stat-card">
          <div className="stat-number">{transactions.length}</div>
          <div className="stat-label">Assigned Transactions</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{transactions.filter(t => t.status === 'pending').length}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{transactions.filter(t => t.status === 'completed').length}</div>
          <div className="stat-label">Completed</div>
        </div>
      </div>

      <div className="portal-tabs">
        <button 
          className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          My Transactions
        </button>
        <button 
          className={`tab ${activeTab === 'tasks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tasks')}
        >
          Task Checklist
        </button>
        <button 
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          My Profile
        </button>
      </div>

      <div className="portal-content">
        {activeTab === 'transactions' && (
          <>
            <div className="section-header">
              <h2>Assigned Transactions</h2>
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : transactions.length === 0 ? (
              <div className="empty-state">
                <h3>No Transactions Yet</h3>
                <p>You haven't been assigned to any transactions yet.</p>
                <p>When a buyer or seller adds you to their transaction, it will appear here.</p>
              </div>
            ) : (
              <div className="transactions-list">
                {transactions.map(transaction => (
                  <div key={transaction.id} className="transaction-card">
                    <div className="transaction-header">
                      <h3>{transaction.listing_title || 'Property Transaction'}</h3>
                      <span className={`badge ${transaction.status === 'completed' ? 'badge-success' : 'badge-warning'}`}>
                        {transaction.status}
                      </span>
                    </div>

                    <div className="transaction-info">
                      <p><strong>Property:</strong> {transaction.listing_address}</p>
                      <p><strong>Buyer:</strong> {transaction.buyer_name}</p>
                      <p><strong>Seller:</strong> {transaction.seller_name}</p>
                      <p><strong>Price:</strong> ${transaction.final_price?.toLocaleString()}</p>
                    </div>

                    <div className="transaction-actions">
                      <button 
                        className="btn btn-primary"
                        onClick={() => { setSelectedTransaction(transaction); setShowDocumentModal(true); }}
                      >
                        Upload Documents
                      </button>
                      <button className="btn btn-secondary">
                        Contact Parties
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'tasks' && (
          <>
            <div className="section-header">
              <h2>Your Task Checklist</h2>
              <p>Standard tasks for {getRoleLabel()}</p>
            </div>

            <div className="tasks-list">
              {getTasksForRole().map(task => (
                <div key={task.id} className="task-item">
                  <label className="task-checkbox">
                    <input type="checkbox" />
                    <span className="checkmark"></span>
                    <span className="task-text">{task.task}</span>
                  </label>
                </div>
              ))}
            </div>

            {getTasksForRole().length === 0 && (
              <div className="empty-state">
                <p>No standard tasks defined for your role.</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'profile' && (
          <>
            <div className="section-header">
              <h2>My Profile</h2>
            </div>

            <div className="profile-card">
              <div className="profile-header">
                <div className="profile-info">
                  <h3>{user.firstName} {user.lastName}</h3>
                  <p className="role-badge">{getRoleLabel()}</p>
                </div>
              </div>

              <div className="profile-details">
                <div className="detail-row">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{user.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{user.phone || 'Not provided'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Role</span>
                  <span className="detail-value">{getRoleLabel()}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Member Since</span>
                  <span className="detail-value">{new Date(user.createdAt || Date.now()).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showDocumentModal && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowDocumentModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload Documents</h3>
              <button className="modal-close" onClick={() => setShowDocumentModal(false)}>X</button>
            </div>
            <div className="modal-body">
              <p>Upload documents for: <strong>{selectedTransaction.listing_title}</strong></p>
              
              <div className="form-group" style={{ marginTop: '20px' }}>
                <label className="btn btn-primary" style={{cursor: 'pointer', display: 'inline-block'}}>
                  {uploadingDocument ? 'Uploading...' : 'Choose File'}
                  <input 
                    type="file" 
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" 
                    onChange={(e) => handleDocumentUpload(e, selectedTransaction.id)}
                    style={{display: 'none'}}
                    disabled={uploadingDocument}
                  />
                </label>
              </div>
              <p style={{color: '#666', fontSize: '14px', marginTop: '10px'}}>
                Supported formats: PDF, DOC, DOCX, JPG, PNG
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ThirdPartyPortal;
